// Atlas-based animation adapted from the supplied autumn-leaves.liquid.
// Each bounded section has an independent lifecycle; the module import is cached.
(() => {
if (customElements.get('sembonia-maple-background')) return;

function createAutumnEffect(target) {
  'use strict';

  /* CHANGE THE LOOK HERE. Keep the atlas filename unchanged after upload. */
  const SETTINGS = {
    textureURL: target.dataset.texture,
    desktopLeaves: 14,
    mobileLeaves: 7,
    speed: 0.85,            // Smaller = slower. Suggested range: 0.3 to 2.
    size: 1,                // Smaller = smaller leaves. Suggested range: 0.5 to 2.
    opacity: 0.7,           // 0 = invisible, 1 = strongest.
    fps: 30
  };

  const events = new AbortController();
  const random = (a, b) => a + Math.random() * (b - a);
  const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
  let THREE, modulePromise, host, layer;
  let renderer, scene, camera, texture, observer, resizeObserver;
  let geometries = [], leaves = [];
  let width = 1, height = 1, mobile = false;
  let generation = 0, loading = false, visible = false;
  let running = false, contextLost = false, time = 0, last = 0;

  function stop() {
    running = false;
    renderer?.setAnimationLoop(null);
  }

  function clearLeaves() {
    leaves.forEach(leaf => {
      scene?.remove(leaf.mesh);
      leaf.mesh.material.dispose();
    });
    leaves = [];
  }

  function cleanup() {
    generation++;
    stop();
    observer?.disconnect();
    resizeObserver?.disconnect();
    observer = resizeObserver = null;
    clearLeaves();
    geometries.forEach(geometry => geometry.dispose());
    geometries = [];
    texture?.dispose();
    renderer?.dispose();
    renderer?.forceContextLoss();
    layer?.remove();
    host = layer = renderer = scene = camera = texture = null;
    loading = visible = contextLost = false;
    width = height = 1;
  }

  function populate() {
    clearLeaves();
    const count = clamp(Math.round(mobile ? SETTINGS.mobileLeaves : SETTINGS.desktopLeaves), 0, 60);
    for (let i = 0; i < count; i++) {
      const depth = random(0.45, 1);
      const tile = depth < 0.6 ? 6 + Math.floor(Math.random() * 2) : Math.floor(Math.random() * 6);
      const material = new THREE.MeshBasicMaterial({
        map: texture,
        transparent: true,
        depthWrite: false,
        depthTest: false,
        side: THREE.DoubleSide,
        forceSinglePass: true,
        toneMapped: false
      });
      const mesh = new THREE.Mesh(geometries[tile], material);
      const size = random(62, 112) * depth * (mobile ? 0.75 : 1) * SETTINGS.size;
      mesh.scale.setScalar(size);
      mesh.renderOrder = Math.round(depth * 1000);
      mesh.rotation.z = random(0, Math.PI * 2);
      scene.add(mesh);
      leaves.push({
        mesh, size, depth,
        x: random(0, width),
        y: random(0, height),
        fall: random(15, 28) * (0.65 + depth * 0.35),
        sway: random(14, 40),
        frequency: random(0.3, 0.65),
        phase: random(0, Math.PI * 2),
        spin: random(-0.18, 0.18),
        alpha: random(0.65, 1)
      });
    }
  }

  function resize() {
    if (!renderer || !host?.isConnected) return;
    const oldWidth = width, oldHeight = height, oldMobile = mobile;
    width = Math.max(1, host.clientWidth);
    height = Math.max(1, host.clientHeight);
    mobile = window.innerWidth < 810;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, mobile ? 1 : 1.5));
    renderer.setSize(width, height, false);
    camera.left = 0;
    camera.right = width;
    camera.top = height;
    camera.bottom = 0;
    camera.updateProjectionMatrix();
    if (!leaves.length || mobile !== oldMobile) populate();
    else leaves.forEach(leaf => {
      leaf.x *= width / oldWidth;
      leaf.y *= height / oldHeight;
    });
    draw(0);
  }

  function draw(dt) {
    time += dt * clamp(SETTINGS.speed, 0, 4);
    leaves.forEach(leaf => {
      leaf.y += leaf.fall * dt * clamp(SETTINGS.speed, 0, 4);
      if (leaf.y > height + leaf.size) {
        leaf.y = -leaf.size;
        leaf.x = random(0, width);
      }
      const wave = time * leaf.frequency + leaf.phase;
      const x = leaf.x + Math.sin(wave) * leaf.sway;
      leaf.mesh.position.set(x, height - leaf.y, leaf.depth * 100);
      leaf.mesh.rotation.x = Math.sin(wave * 0.8) * 0.32;
      leaf.mesh.rotation.y = Math.cos(wave * 0.65) * 0.45;
      leaf.mesh.rotation.z += leaf.spin * dt * SETTINGS.speed;
      const fade = clamp(Math.min(leaf.y / 55, (height - leaf.y) / 75), 0, 1);
      leaf.mesh.material.opacity = clamp(SETTINGS.opacity, 0, 1) * leaf.alpha * fade;
    });
    renderer.render(scene, camera);
  }

  function frame(now) {
    if (!last) { last = now; draw(0); return; }
    const elapsed = now - last;
    const interval = 1000 / clamp(SETTINGS.fps, 10, 60);
    if (elapsed < interval - 0.5) return;
    last = now;
    draw(Math.min(elapsed / 1000, 0.1));
  }

  function sync() {
    const shouldRun = visible && !document.hidden && !contextLost;
    if (!renderer) {
      if (visible && !document.hidden && !loading && host?.isConnected) initialize();
      return;
    }
    if (shouldRun && !running) {
      last = 0;
      running = true;
      renderer.setAnimationLoop(frame);
    } else if (!shouldRun) stop();
  }


  async function initialize() {
    loading = true;
    const current = generation;
    const valid = () => current === generation && host?.isConnected;
    try {
      // Pinned ES module; no import map and no global THREE required.
      modulePromise ||= import('https://cdn.jsdelivr.net/npm/three@0.180.0/build/three.module.min.js')
        .catch(error => { modulePromise = null; throw error; });
      THREE = await modulePromise;
      if (!valid()) return;
      const loadedTexture = await new THREE.TextureLoader().loadAsync(SETTINGS.textureURL);
      if (!valid()) { loadedTexture.dispose(); return; }
      texture = loadedTexture;
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.generateMipmaps = false;
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false, powerPreference: 'low-power' });
      renderer.setClearColor(0x000000, 0);
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      scene = new THREE.Scene();
      camera = new THREE.OrthographicCamera(0, 1, 1, 0, 0.1, 2000);
      camera.position.z = 1000;

      // One shared image; each geometry selects a different atlas tile.
      for (let i = 0; i < 8; i++) {
        const geometry = new THREE.PlaneGeometry(1, 1);
        const uv = geometry.attributes.uv;
        const column = i % 4, row = Math.floor(i / 4);
        for (let j = 0; j < uv.count; j++) {
          uv.setXY(j, (column + uv.getX(j)) / 4, (1 - row + uv.getY(j)) / 2);
        }
        uv.needsUpdate = true;
        geometries.push(geometry);
      }

      layer = document.createElement('div');
      layer.className = 'sb-autumn-layer';
      layer.style.cssText = 'position:absolute;inset:0;overflow:hidden;pointer-events:none';
      renderer.domElement.style.cssText = 'display:block;width:100%;height:100%;pointer-events:none';
      layer.setAttribute('aria-hidden', 'true');
      layer.appendChild(renderer.domElement);
      host.appendChild(layer);

      renderer.domElement.addEventListener('webglcontextlost', event => {
        if (current !== generation) return;
        event.preventDefault();
        contextLost = true;
        sync();
      });
      renderer.domElement.addEventListener('webglcontextrestored', () => {
        if (current !== generation) return;
        contextLost = false;
        resize();
        sync();
      });
      resize();
      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(host);
      }
      loading = false;
      sync();
    } catch (error) {
      if (valid()) {
        console.warn('[Autumn leaves] Effect skipped. Check the atlas filename, footer selector, CDN access, and WebGL support.', error);
        cleanup();
      }
    }
  }

  function mount() {
    cleanup();
    host = target;
    if (!host) {
      console.warn('[Autumn leaves] Target not found:', target);
      return;
    }
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        visible = entries[0].isIntersecting;
        sync();
      });
      observer.observe(host);
    } else { visible = true; sync(); }
  }

  const listen = (target, name, handler) => target.addEventListener(name, handler, { signal: events.signal });
  listen(document, 'visibilitychange', sync);
  listen(window, 'resize', resize);
  mount();
  return () => { events.abort(); cleanup(); };
}

customElements.define('sembonia-maple-background', class extends HTMLElement {
  connectedCallback() {
    this.disposeEffect?.();
    this.disposeEffect = createAutumnEffect(this);
  }
  disconnectedCallback() {
    this.disposeEffect?.();
    this.disposeEffect = null;
  }
});
})();
